import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IWidgetDropdownData } from "../models/interfaces";
import { DropDownTypes, IFilterData } from "src/pages/Insights/models/interfaces";

interface widgetState {
	isWidgetOpen: boolean;
	isWidgetLoading: boolean;
	isWidgetExpanded: boolean;
	widgetHeading: { [key: string]: string };
	widgetText: { [key: string]: string };
	widgetDropdownData?: IWidgetDropdownData;
	widgetStudyPayload: {
		study_id?: string;
		chart_panel_dropdown?: string;
		split_by?: {
			split_by_field?: string | null;
			split_values?: any[] | null;
			is_numerical?: boolean;
		};
		filters?: any[];
		question_filters?: any[];
		waves?: any;
		is_segment?: boolean;
		page?: number;
		page_size?: number;
		request_source?: string;
		config_collection_data?: any;
	};
	openWidgetProps: {
		query_id?: string;
		data?: {};
		widget_id?: string;
	};
	isFilterSplitByUpdated: number;
	widgetSelectedSegments: string[];
	widgetSelectedSegments___: string[];
	widgetAllFiltersData: IFilterData[];
	widgetSelectedFilters: string[];
	widgetSelectedFiltersArray: string[];
	widgetIsFilterFocused: boolean;
	widgetActiveDrawerTabKey: DropDownTypes;
	widgetOpenDrawer: boolean;
	widgetIsCompareActive: boolean;
	widgetActiveSegments: string[];
	widgetChartDataPointSelections: { [chartId: string]: any };
	isStudyAccessRevoked: boolean;
	widgetError: boolean;
	widgetErrorText: string;
}

const initialState: widgetState = {
	isWidgetOpen: false,
	isWidgetLoading: false,
	isWidgetExpanded: false,
	widgetHeading: {},
	widgetText: {},
	widgetStudyPayload: {},
	openWidgetProps: {},
	isFilterSplitByUpdated: 0,
	widgetSelectedSegments: [],
	widgetSelectedSegments___: [],
	widgetAllFiltersData: [],
	widgetSelectedFilters: [],
	widgetSelectedFiltersArray: [],
	widgetIsFilterFocused: false,
	widgetActiveDrawerTabKey: DropDownTypes.Filters,
	widgetOpenDrawer: false,
	widgetIsCompareActive: false,
	widgetActiveSegments: [],
	widgetChartDataPointSelections: {},
	isStudyAccessRevoked: false,
	widgetError: false,
	widgetErrorText: "",
};

const widgetSlice = createSlice({
	name: "widget",
	initialState,
	reducers: {
		setWidgetOpen(state, action: PayloadAction<boolean>) {
			state.isWidgetOpen = action.payload;
		},
		setWidgetLoading(state, action: PayloadAction<boolean>) {
			state.isWidgetLoading = action.payload;
		},
		setIsWidgetExpanded(state, action: PayloadAction<boolean>) {
			state.isWidgetExpanded = action.payload;
		},
		toggleWidgetExpanded(state) {
			state.isWidgetExpanded = !state.isWidgetExpanded;
		},
		setOpenWidgetProps(state, action: PayloadAction<Partial<{ query_id?: string; data?: {}; widget_id: string }>>) {
			state.openWidgetProps = { ...state.openWidgetProps, ...action.payload };
		},
		updateOpenWidgetProps(state, action: PayloadAction<{ data?: {}; query_id?: string }>) {
			state.openWidgetProps = { ...state.openWidgetProps, ...action.payload };
		},
		resetAllWidgetProps(state) {
			state.isWidgetOpen = false;
			state.isWidgetExpanded = false;
			state.openWidgetProps = {};
			state.widgetHeading = {};
			state.widgetText = {};
			state.widgetStudyPayload = {};
			state.isFilterSplitByUpdated = 0;
			state.widgetSelectedSegments = [];
			state.widgetSelectedSegments___ = [];
			state.widgetAllFiltersData = [];
			state.widgetSelectedFilters = [];
			state.widgetSelectedFiltersArray = [];
			state.widgetIsFilterFocused = false;
			state.widgetActiveDrawerTabKey = DropDownTypes.Filters;
			state.widgetOpenDrawer = false;
			state.widgetIsCompareActive = false;
			state.widgetActiveSegments = [];
			state.isWidgetLoading = false;
			state.widgetDropdownData = {};
			state.widgetChartDataPointSelections = {};
			state.isStudyAccessRevoked = false;
			state.widgetError = false;
			state.widgetErrorText = "";
		},
		setWidgetHeading(state, action: PayloadAction<{ [key: string]: string }>) {
			state.widgetHeading = action.payload;
		},
		resetWidgetHeading(state) {
			state.widgetHeading = {};
		},
		setWidgetText(state, action: PayloadAction<{ [key: string]: string }>) {
			state.widgetText = action.payload;
		},
		resetWidgetText(state) {
			state.widgetText = {};
		},
		setWidgetStudyPayload(state, action: PayloadAction<widgetState["widgetStudyPayload"]>) {
			state.widgetStudyPayload = action.payload;
		},
		updateWidgetStudyPayload(state, action: PayloadAction<Partial<widgetState["widgetStudyPayload"]>>) {
			state.widgetStudyPayload = { ...state.widgetStudyPayload, ...action.payload };
		},
		resetWidgetStudyPayload(state) {
			state.widgetStudyPayload = {};
		},
		setWidgetDropdownData(state, action: PayloadAction<IWidgetDropdownData>) {
			state.widgetDropdownData = action.payload;
		},
		setIsFilterSplitByUpdated: (state, action: PayloadAction<number | undefined>) => {
			if (typeof action.payload === "number") {
				state.isFilterSplitByUpdated = action.payload;
			} else {
				state.isFilterSplitByUpdated += 1;
			}
			state.widgetError = false;
			state.widgetErrorText = "";
		},
		setWidgetSelectedSegments: (state, action) => {
			state.widgetSelectedSegments = action.payload;
		},
		setWidgetSelectedSegments___: (state, action) => {
			state.widgetSelectedSegments___ = action.payload;
		},
		setWidgetAllFiltersData: (state, action: PayloadAction<IFilterData[]>) => {
			state.widgetAllFiltersData = action.payload; // Fixed the path
		},
		setWidgetIsFilterFocused(state, action: PayloadAction<boolean>) {
			state.widgetIsFilterFocused = action.payload;
		},
		setWidgetSelectedFilters(state, action: PayloadAction<string[]>) {
			state.widgetSelectedFilters = action.payload;
		},
		setWidgetSelectedFiltersArray(state, action: PayloadAction<string[]>) {
			state.widgetSelectedFiltersArray = action.payload;
		},
		setWidgetActiveDrawerTabKey: (state, action: PayloadAction<DropDownTypes>) => {
			state.widgetActiveDrawerTabKey = action.payload;
		},
		setWidgetOpenDrawer(state, action: PayloadAction<boolean>) {
			state.widgetOpenDrawer = action.payload;
		},
		setWidgetIsCompareActive(state, action: PayloadAction<boolean>) {
			state.widgetIsCompareActive = action.payload;
		},
		setWidgetActiveSegments(state, action: PayloadAction<string[]>) {
			state.widgetActiveSegments = action.payload;
		},
		setWidgetChartDataPointSelections: (state, action) => {
			const { chartId, selectedPoints } = action.payload;
			state.widgetChartDataPointSelections[chartId] = selectedPoints;
		},
		setIsStudyAccessRevoked: (state, action: PayloadAction<boolean>) => {
			state.isStudyAccessRevoked = action.payload;
		},
		setWidgetError: (state, action: PayloadAction<boolean>) => {
			state.widgetError = action.payload;
		},
		setWidgetErrorText: (state, action: PayloadAction<string>) => {
			state.widgetErrorText = action.payload;
		},
		// You can add more reducers here if needed
	},
});

export const {
	setWidgetOpen,
	setWidgetLoading,
	setIsWidgetExpanded,
	toggleWidgetExpanded,
	setOpenWidgetProps,
	updateOpenWidgetProps,
	resetAllWidgetProps,
	setWidgetHeading,
	resetWidgetHeading,
	setWidgetText,
	resetWidgetText,
	setWidgetStudyPayload,
	updateWidgetStudyPayload,
	resetWidgetStudyPayload,
	setWidgetDropdownData,
	setIsFilterSplitByUpdated,
	setWidgetSelectedSegments,
	setWidgetSelectedSegments___,
	setWidgetAllFiltersData,
	setWidgetIsFilterFocused,
	setWidgetSelectedFilters,
	setWidgetSelectedFiltersArray,
	setWidgetActiveDrawerTabKey,
	setWidgetOpenDrawer,
	setWidgetIsCompareActive,
	setWidgetActiveSegments,
	setWidgetChartDataPointSelections,
	setIsStudyAccessRevoked,
	setWidgetError,
	setWidgetErrorText,
} = widgetSlice.actions;

export default widgetSlice.reducer;
